import {Component, Input, OnDestroy} from '@angular/core';
import {SearchFilters} from '@interface/search.filters';
import {Product} from '@interface/product';
import {SearchService} from '@service/search/search.service';
import {Router} from '@angular/router';
import {ModelService} from '@service/model/model.service';
import {CommonService} from '@service/common/common.service';
import {HeaderStep} from '@enum/header.step';
import {HeaderService} from '@service/header/header.service';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {OrderService} from '@service/order/order.service';
import {Pagination} from '@interface/pagination';
import {TrackEventsService} from '@service/track-events/track-events.service';

interface SearchComposer {
  modelName: string;
  modelNameId: string;
  slugModular: string;
}

interface SearchProduct {
  productId: number;
  productName: string;
  modelCode: string;
  modelElementCode: string;
  productDescription: string;
  imagePath: string;
  images: {
    list: string[],
    indexPos: number
  };
  productState: string;
  ext_links: string[];
  imageLoaded?: boolean;
  composer?: SearchComposer | null;
}

type SearchItem =
  | { type: 'composer'; composer: SearchComposer }
  | { type: 'product'; product: SearchProduct };

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  standalone: false
})
export class SearchComponent implements OnDestroy {
  @Input() startLoading = false;
  private destroy$ = new Subject<void>();

  public products: SearchProduct[] | null = null;
  public searchItems: SearchItem[] = [];
  public loaded = !this.startLoading;
  public showMore = false;
  public pagination = SearchComponent.defaultPagination();
  public lastFilters?: SearchFilters;

  constructor(
    private searchService: SearchService,
    private router: Router,
    private modelService: ModelService,
    private common: CommonService,
    private headerService: HeaderService,
    private orderService: OrderService,
    private trackEventsService: TrackEventsService
  ) {
    this.searchService.filtersData
      .pipe(takeUntil(this.destroy$))
      .subscribe(({filters, track}) => {
        this.pagination = filters?.pagination || SearchComponent.defaultPagination();
        this.searchProducts(filters || undefined, track);
      });
  }

  private static defaultPagination(): Pagination {
    return {
      max: 52,
      offset: 0
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get lastSearchedItem(): number {
    return this.modelService.lastSearchedItem;
  }

  public productDetail(product: Product): void {
    this.router.navigate(['model-detail', String(product?.id)]);
  }

  public modelDetail(modelElement: SearchProduct): void {
    this.headerService.header.next(HeaderStep.CONFIGURATION);
    this.modelService.lastSearchedItem = modelElement.productId;
    this.router.navigate(['model-configuration', String(modelElement.productId)]);
  }

  public composerDetail(composer: SearchComposer): void {
    this.router.navigate(['model-detail', composer.slugModular]);
  }

  public convertProductImage(model: SearchProduct, skip?: boolean): string {
    return this.common.convertProductImage(model, skip);
  }

  public searchMore(): void {
    this.pagination.offset += this.pagination.max;
    this.searchProducts(this.lastFilters);
  }

  public searchUndo(): void {
    this.pagination.offset = Math.max(this.pagination.offset - this.pagination.max, 0);
    this.searchProducts(this.lastFilters);
  }

  public imageLoaded(model: SearchProduct): void {
    model.imageLoaded = true;
  }

  private buildComposerItems(products: SearchProduct[] = []): SearchItem[] {
    const composerMap = new Map<string, SearchComposer>();

    for (const product of products) {
      const composer = product.composer;

      if (!composer?.slugModular) {
        continue;
      }

      if (!composerMap.has(composer.slugModular)) {
        composerMap.set(composer.slugModular, {
          modelName: composer.modelName,
          modelNameId: composer.modelNameId,
          slugModular: composer.slugModular
        });
      }
    }

    return Array.from(composerMap.values()).map((composer) => ({
      type: 'composer' as const,
      composer
    }));
  }

  private buildSearchItems(products: SearchProduct[] = []): SearchItem[] {
    const composerItems = this.buildComposerItems(products);
    const productItems = products.map((product) => ({
      type: 'product' as const,
      product
    }));

    return [...composerItems, ...productItems];
  }

  private searchProducts(searchFilters?: SearchFilters, track = false): void {
    if (searchFilters) {
      this.lastFilters = searchFilters;
      this.loaded = false;
      searchFilters.pagination = this.pagination;

      this.modelService.search(searchFilters)
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          if (data?.error) {
            this.common.showErrorToast(data?.error);
            return;
          }

          this.loaded = true;
          this.products = data?.products ?? [];
          this.searchItems = this.buildSearchItems(this.products);
          this.showMore = data?.moreProducts;

          if (track) {
            this.trackEventsService.filterInteractionConfigurator({
              code: searchFilters.productCode,
              name: searchFilters.name,
              category: data.category,
              season: data.season
            });
          }
        });
    } else {
      this.modelService.resetPreviousFilter();
      this.loaded = true;
      this.lastFilters = undefined;
      this.products = null;
      this.searchItems = [];
    }
  }

  public isCopyingConfiguration(): boolean {
    return !!this.orderService.copyConfigurationId;
  }

  public cancelCopyingConfiguration(): void {
    this.orderService.copyConfigurationId = null;
  }
}
